import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { HiArrowRight, HiMagnifyingGlass, HiXMark, HiUser, HiTicket, HiCalendarDays, HiDocumentText, HiCheckCircle, HiBanknotes } from 'react-icons/hi2'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog'
import { apiClient } from '../../common/apiClient'
import { canOpenResource, rankResources, type Resource, type SearchCategory } from './model'
import type { AccessFlags } from '../../contexts/UserPermissionsContext'
import { useProfilePictures } from '../../contexts/ProfilePictureContext'
import ResourceIcon from './ResourceIcon'
import { GROUPED_SEARCH_CATEGORIES, groupedSearchCategory, withoutDuplicateTitles } from '../hub-links/searchGroups'

type DataRow = { id: string | number; title: string; subtitle?: string; path: string; email?: string; meta?: string[]; metaStyle?: 'default' | 'amount' }
type Source = 'people' | 'bills' | 'tasks' | 'documents' | 'leave' | 'tickets' | 'calendar' | 'memos'
type Results = Record<Source, DataRow[]>
type SearchResult = Resource & { email?: string; meta?: string[]; metaStyle?: 'default' | 'amount' }
const sources: Record<Source, { category: SearchCategory; icon: Resource['icon'] }> = {
  people: { category: 'People', icon: HiUser }, bills: { category: 'Bills', icon: HiBanknotes },
  tasks: { category: 'Tasks', icon: HiCheckCircle }, documents: { category: 'Documents', icon: HiDocumentText },
  leave: { category: 'Leave', icon: HiCalendarDays }, tickets: { category: 'IT Tickets', icon: HiTicket },
  calendar: { category: 'Calendar', icon: HiCalendarDays }, memos: { category: 'Memos', icon: HiDocumentText },
}
// Results are filed under the same groups as the header menus and home sidebar.
const categoryOf = (item: SearchResult) => groupedSearchCategory(item)

export default function HomeSearch({ initialQuery, initialCategory = 'All', resources, email, ready, flags, documentsVisible, loading, errors, onOpen, onClose }: {
  initialQuery: string; initialCategory?: string; resources: Resource[]; email: string;
  ready: boolean; flags: AccessFlags; documentsVisible: boolean; loading: boolean; errors: string[];
  onOpen: (resource: Resource) => void; onClose: () => void;
}) {
  const [query, setQuery] = useState(initialQuery)
  const [debounced, setDebounced] = useState(initialQuery)
  const [category, setCategory] = useState<string>(initialCategory)
  const [selected, setSelected] = useState(0)
  const resultsRef = useRef<HTMLDivElement>(null)
  const { getProfilePicture, fetchProfilePictures } = useProfilePictures()
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 250)
    return () => clearTimeout(timer)
  }, [query])
  const records = useQuery({
    queryKey: ['home-preview-search', email, debounced.trim()],
    enabled: ready && debounced.trim().length >= 2,
    queryFn: ({ signal }) => apiClient.get<Results>('/api/search', { params: { q: debounced.trim(), email }, signal }), retry: 1,
    refetchOnWindowFocus: false,
  })
  const matches = useMemo(() => {
    const local = rankResources(resources, query).filter(item => canOpenResource(item.path, flags, documentsVisible))
    const remote: SearchResult[] = ready && query.trim().length >= 2 && query === debounced
      ? (Object.keys(sources) as Source[]).flatMap(source => (records.data?.[source] || []).map(row => ({
        id: `${source}-${row.id}`, label: row.title, path: row.path, detail: row.subtitle || sources[source].category,
        kind: source === 'people' ? 'People' as const : 'Records' as const,
        searchCategory: sources[source].category, icon: sources[source].icon,
        email: source === 'people' ? row.email : undefined, meta: row.meta, metaStyle: row.metaStyle,
      }))).filter(item => canOpenResource(item.path, flags, documentsVisible)) : []
    const seen = new Set<string>()
    const unique = [...local, ...remote].filter(item => {
      if (seen.has(item.path)) return false
      seen.add(item.path)
      return true
    }) as SearchResult[]
    // The logo and sidebar already go home, so search skips the Home page itself.
    return withoutDuplicateTitles(unique.filter(item => item.path !== '/'))
  }, [resources, query, debounced, records.data, flags, documentsVisible, ready])
  const results = useMemo(() => {
    if (category !== 'All') return matches.filter(item => categoryOf(item) === category)
    // "All" shows a heading per group, in the order each group's best match appears.
    const order: string[] = []
    const byCategory = new Map<string, SearchResult[]>()
    for (const item of matches) {
      const key = categoryOf(item)
      if (!byCategory.has(key)) { byCategory.set(key, []); order.push(key) }
      byCategory.get(key)!.push(item)
    }
    // Individual bills can be numerous, so they stay last.
    return [...order.filter(key => key !== 'Bills'), ...order.filter(key => key === 'Bills')].flatMap(key => byCategory.get(key)!)
  }, [matches, category])
  const visibleResults = results.slice(0, 40)
  const counts = useMemo(() => {
    const values = new Map<string, number>([['All', matches.length]])
    for (const item of matches) values.set(categoryOf(item), (values.get(categoryOf(item)) || 0) + 1)
    return values
  }, [matches])
  useEffect(() => setSelected(0), [query, category, email])
  const activeIndex = Math.min(selected, Math.max(0, results.length - 1))
  useEffect(() => {
    resultsRef.current?.querySelector<HTMLElement>('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, query, category])
  useEffect(() => {
    const emails = matches.flatMap(item => item.email ? [item.email] : [])
    if (emails.length) void fetchProfilePictures(emails)
  }, [matches, fetchProfilePictures])
  const searching = loading || (ready && query.trim().length >= 2 && (query !== debounced || records.isFetching))
  const renderResult = (item: SearchResult, index: number) => <button type="button" key={item.path}
    className={`hp-search-result ${activeIndex === index ? 'hp-search-result-selected' : ''}`}
    data-selected={activeIndex === index}
    onMouseEnter={() => setSelected(index)} onFocus={() => setSelected(index)} onClick={() => onOpen(item)}>
    <ResourceIcon resource={{ ...item, imageSrc: item.email ? getProfilePicture(item.email) : item.imageSrc }} />
    <span className="hp-result-copy"><strong>{item.label}</strong><span>{item.detail}</span></span>
    {item.meta?.length ? <span className={`hp-result-meta ${item.metaStyle === 'amount' ? 'hp-result-amount' : ''}`}>
      <strong>{item.meta[0]}</strong>{item.meta[1] && <span>{item.meta[1]}</span>}
    </span> : null}
    <HiArrowRight className="hp-result-arrow" />
  </button>

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose() }}>
      <DialogContent className="hp-dialog hp-search-dialog">
        <DialogTitle className="sr-only">Search Duo Hub</DialogTitle>
        <DialogDescription className="sr-only">Find profile details, apps, administration, policies, people, events, memos and your records.</DialogDescription>
        <div className="hp-search-input-row">
          <HiMagnifyingGlass aria-hidden="true" />
          <input autoFocus aria-label="Search Duo Hub" placeholder="Search" value={query}
            onFocus={event => event.currentTarget.select()}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'ArrowDown') { event.preventDefault(); setSelected(Math.min(activeIndex + 1, Math.max(0, Math.min(results.length, 40) - 1))) }
              if (event.key === 'ArrowUp') { event.preventDefault(); setSelected(Math.max(activeIndex - 1, 0)) }
              if (event.key === 'Enter' && results[activeIndex]) { event.preventDefault(); onOpen(results[activeIndex]) }
            }} />
          <button type="button" className="hp-icon-button" aria-label="Close search" onClick={onClose}><HiXMark /></button>
        </div>
        <div className="hp-search-body">
          <nav className="hp-search-categories" aria-label="Search categories">
            {GROUPED_SEARCH_CATEGORIES.filter(filter => filter === 'All' || filter === category || (counts.get(filter) || 0) > 0).map(filter =>
              <button type="button" key={filter} aria-label={`${filter} ${counts.get(filter) || 0}`} aria-pressed={category === filter}
                className={category === filter ? 'hp-search-category hp-search-category-active' : 'hp-search-category'}
                onClick={() => setCategory(filter)}><span>{filter}</span><span>{counts.get(filter) || 0}</span></button>)}
          </nav>
          <div className="hp-search-results" ref={resultsRef}>
            <div className="hp-search-caption" aria-live="polite">
              {query ? `${results.length} results${searching ? ' · Searching…' : ''}` : 'Browse Duo Hub or start typing'}
            </div>
            {(records.isError || errors.length > 0) && <p className="hp-notice">Some results are unavailable. {errors.length ? `Could not load ${errors.join(', ')}.` : 'People and records could not be loaded.'}</p>}
            {visibleResults.map((item, index) => {
              const key = categoryOf(item)
              const startsGroup = category === 'All' && (index === 0 || categoryOf(visibleResults[index - 1]) !== key)
              return <Fragment key={item.path}>
                {startsGroup && <h3 className="hp-search-group-heading hp-search-grouped-heading">{key} <span>{counts.get(key) || 0}</span></h3>}
                {renderResult(item, index)}
              </Fragment>
            })}
            {!results.length && <div className="hp-empty"><HiMagnifyingGlass /><strong>{searching ? 'Searching Duo Hub…' : 'No matches yet'}</strong>
              <p>{searching ? 'Your results will appear here.' : 'Try a shorter phrase, a person’s name, or browse another category.'}</p></div>}
            {results.length > 40 && <p className="hp-search-caption">Showing the first 40 results. Add another word or select a category to narrow your search.</p>}
          </div>
        </div>
        <div className="hp-search-footer"><span>↑ ↓ to move · Enter to open</span><span>Esc to close</span></div>
      </DialogContent>
    </Dialog>
  )
}
